import { DEVICE_CATEGORIES } from '@/constants/device-constants';
import { getReaders } from '@/services/readerService';
import { useFormikContext } from 'formik';
import type { FC } from 'react';
import React, { useState } from 'react';
import { DEF_PG_SIZE_SUB_MIN } from '../../constants/maximum-page-size';
import { useLocale } from '../../contexts/localeContext';
import { TableContextProvider } from '../../contexts/table/TableContextProvider';
import { TranslationKeys } from '../../contexts/TranslationKeys';
import LiftReaderColumns from '../../models/ColumnDefinitions/LiftReaderColumns';
import { ListQuery } from '../../models/ListQuery';
import { DeviceImprox } from '../../models/DeviceImprox';
import ReaderSearch from '../../models/search/ReaderSearch';
import { hideColumns } from '../../models/utilities/columnHelper';
import { appendFilter } from '../../utilities/filters';
import TableTab from '../tabs/table-tab';
import { WizardStepLayout } from '../wizard/wizard-summary-layout';

interface ReaderStepProps {
    onNext: (event: any) => void;
    onBack: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    setStepDescriptions: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>;
}

const ReaderStep: FC<ReaderStepProps> = ({ onNext, onBack, setStepDescriptions }) => {
    const { translate } = useLocale();
    const { setFieldValue, values } = useFormikContext<any>();
    const [taskSelected, setTaskSelected] = useState(false);
    const [reader, setReader] = useState<DeviceImprox>({
            id: 0,
            name: '',
            nameOriginal: '',
            fixedAddress: '',
        } as DeviceImprox);

    const onLocalNext = (event: any) => {
        if (taskSelected) {
            setFieldValue("readerId", reader?.id);
            setFieldValue("readerName", reader?.nameOriginal);
            setFieldValue("readerFixedAddress", reader?.fixedAddress);
            onNext({ ...event });
            setStepDescriptions(prev => ({ ...prev, step4: reader?.nameOriginal || '' }));
        }
    }

    const getFilters = (listQuery?: ListQuery) => {
        const notUsedByLiftFilter = appendFilter(false, 'Used By Lift', 'usedByLift', listQuery?.filters ?? []);
        const directionOfEntry = appendFilter(0, "Direction Of Entry", 'devDirection', notUsedByLiftFilter);
        const deviceTypeFilter = appendFilter(`${DEVICE_CATEGORIES.READER},${DEVICE_CATEGORIES.LOCK},${DEVICE_CATEGORIES.EL_LOCK_ENTRY}`, "Category", "unitType.utCategory", directionOfEntry);
        return deviceTypeFilter;
    }

    const itemSelected = (selectedItem: any) => {
        if (selectedItem) {
            setReader({ ...selectedItem } as DeviceImprox);
            setTaskSelected(true);
            onNext({}); //fake event
            setStepDescriptions(prev => ({ ...prev, step4: selectedItem.nameOriginal }));
            setFieldValue("readerId", selectedItem.id);
            setFieldValue("readerName", selectedItem.nameOriginal);
            setFieldValue("readerFixedAddress", selectedItem.fixedAddress);
        }
    }

    return <WizardStepLayout onBack={onBack} onNext={onLocalNext} nextDisabled={!taskSelected}>
        <div className='flex w-full overflow-x-auto' style={{ maxHeight: '410px' }}>
            <TableContextProvider
                dataMethod={(listParameter?: ListQuery) => getReaders(translate, { ...listParameter, filters: getFilters(listParameter)})}
                tableDescriptor={hideColumns(LiftReaderColumns, ['type', 'devLicense', 'devReaderSerial', 'site', 'door', 'preset', 'musteringPointLabel'])}
                searchDescriptor={ReaderSearch}
                useTable
                usePagination
                useFilter
                useSearch
                useSort
                maxPageSize={DEF_PG_SIZE_SUB_MIN}
                canExportReport={false}>
                <TableTab
                    title={translate.byKey(TranslationKeys.selectReader)}
                    selectRows={false}
                    onRowClicked={(data) => (itemSelected(data))}
                    tableId={'liftReaderTable'}
                    cyName="reader"
                    />
            </TableContextProvider>
        </div>
    </WizardStepLayout>
}

export default ReaderStep;